// Validation utilities for Mawered application

import i18n from '../i18n';

const t = (key, options) => i18n.t(key, options);

/**
 * Common validation rules
 */
export const ValidationRules = {
  // Required field validation
  required: (value, fieldName) => {
    const label = fieldName || t("validation.thisField");
    if (!value || (typeof value === "string" && !value.trim())) {
      return t("validation.required", { field: label });
    }
    return null;
  },

  // Email validation
  email: (value) => {
    if (!value) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return t("validation.emailInvalid");
    }
    return null;
  },

  // Phone number validation (international format)
  phone: (value) => {
    if (!value) return null;
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(value.replace(/[\s\-\(\)]/g, ""))) {
      return t("validation.phoneInvalid");
    }
    return null;
  },

  // URL validation
  url: (value) => {
    if (!value) return null;
    try {
      new URL(value);
      return null;
    } catch {
      return t("validation.urlInvalid");
    }
  },

  // French SIRET validation (14 digits)
  siret: (value) => {
    if (!value) return null;
    const siretRegex = /^\d{14}$/;
    if (!siretRegex.test(value)) {
      return t("validation.siretInvalid");
    }
    return null;
  },

  // French SIREN validation (9 digits)
  siren: (value) => {
    if (!value) return null;
    const sirenRegex = /^\d{9}$/;
    if (!sirenRegex.test(value)) {
      return t("validation.sirenInvalid");
    }
    return null;
  },

  // Minimum length validation
  minLength: (min) => (value) => {
    if (!value) return null;
    if (value.length < min) {
      return t("validation.minLength", { min });
    }
    return null;
  },

  // Maximum length validation
  maxLength: (max) => (value) => {
    if (!value) return null;
    if (value.length > max) {
      return t("validation.maxLength", { max });
    }
    return null;
  },

  // Number range validation
  numberRange: (min, max) => (value) => {
    if (!value) return null;
    const num = parseFloat(value);
    if (isNaN(num)) {
      return t("validation.numberRequired");
    }
    if (num < min || num > max) {
      return t("validation.numberRange", { min, max });
    }
    return null;
  },

  // Positive number validation
  positiveNumber: (value) => {
    if (!value) return null;
    const num = parseFloat(value);
    if (isNaN(num) || num <= 0) {
      return t("validation.positiveNumber");
    }
    return null;
  },

  // Date validation
  date: (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return t("validation.dateInvalid");
    }
    return null;
  },

  // Date range validation
  dateRange: (minDate, maxDate) => (value) => {
    if (!value) return null;
    const date = new Date(value);
    const min = new Date(minDate);
    const max = new Date(maxDate);

    if (date < min || date > max) {
      return t("validation.dateRange", {
        min: min.toLocaleDateString(i18n.language),
        max: max.toLocaleDateString(i18n.language),
      });
    }
    return null;
  },

  // Password strength validation
  password: (value) => {
    if (!value) return null;
    const minLength = 8;
    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);
    const hasNumbers = /\d/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

    if (value.length < minLength) {
      return t("validation.passwordMinLength", { min: minLength });
    }
    if (!hasUppercase || !hasLowercase) {
      return t("validation.passwordCase");
    }
    if (!hasNumbers) {
      return t("validation.passwordNumber");
    }
    if (!hasSpecialChar) {
      return t("validation.passwordSpecial");
    }
    return null;
  },

  // Custom regex validation
  regex: (pattern, message) => (value) => {
    if (!value) return null;
    if (!pattern.test(value)) {
      return message;
    }
    return null;
  },

  // Match field validation (for password confirmation)
  matches: (otherValue, fieldName) => (value) => {
    if (value !== otherValue) {
      return t("validation.fieldsMatch", { field: fieldName });
    }
    return null;
  },
};

/**
 * HR-specific validation rules
 */
export const HRValidationRules = {
  // Employee ID validation
  employeeId: (value) => {
    if (!value) return null;
    const idRegex = /^[A-Z]{2,3}\d{4,6}$/;
    if (!idRegex.test(value)) {
      return t("validation.empIdInvalid");
    }
    return null;
  },

  // Badge code validation
  badgeCode: (value) => {
    if (!value) return null;
    const badgeRegex = /^[A-Z]{2}\d{6}$/;
    if (!badgeRegex.test(value)) {
      return t("validation.badgeInvalid");
    }
    return null;
  },

  // Working hours validation
  workingHours: (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return t("validation.numberRequired");
    if (num < 0 || num > 24) {
      return t("validation.workingHoursRange");
    }
    return null;
  },

  // Salary validation
  salary: (value) => {
    if (!value) return null;
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) {
      return t("validation.salaryPositive");
    }
    if (num > 999999) {
      return t("validation.salaryTooHigh");
    }
    return null;
  },

  // Social security number validation (French format)
  socialSecurity: (value) => {
    if (!value) return null;
    const ssRegex = /^[12][0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])[0-9]{8}$/;
    if (!ssRegex.test(value.replace(/\s/g, ""))) {
      return t("validation.ssInvalid");
    }
    return null;
  },
};

/**
 * Validation schema builder
 */
export class ValidationSchema {
  constructor() {
    this.rules = {};
  }

  field(fieldName) {
    this.currentField = fieldName;
    this.rules[fieldName] = [];
    return this;
  }

  required(message) {
    if (this.currentField) {
      this.rules[this.currentField].push((value) => ValidationRules.required(value, message || this.currentField));
    }
    return this;
  }

  email() {
    if (this.currentField) {
      this.rules[this.currentField].push(ValidationRules.email);
    }
    return this;
  }

  phone() {
    if (this.currentField) {
      this.rules[this.currentField].push(ValidationRules.phone);
    }
    return this;
  }

  url() {
    if (this.currentField) {
      this.rules[this.currentField].push(ValidationRules.url);
    }
    return this;
  }

  siret() {
    if (this.currentField) {
      this.rules[this.currentField].push(ValidationRules.siret);
    }
    return this;
  }

  siren() {
    if (this.currentField) {
      this.rules[this.currentField].push(ValidationRules.siren);
    }
    return this;
  }

  minLength(min) {
    if (this.currentField) {
      this.rules[this.currentField].push(ValidationRules.minLength(min));
    }
    return this;
  }

  maxLength(max) {
    if (this.currentField) {
      this.rules[this.currentField].push(ValidationRules.maxLength(max));
    }
    return this;
  }

  numberRange(min, max) {
    if (this.currentField) {
      this.rules[this.currentField].push(ValidationRules.numberRange(min, max));
    }
    return this;
  }

  custom(validationFn) {
    if (this.currentField) {
      this.rules[this.currentField].push(validationFn);
    }
    return this;
  }

  validate(data) {
    const errors = {};

    for (const [fieldName, validators] of Object.entries(this.rules)) {
      const value = data[fieldName];

      for (const validator of validators) {
        const error = validator(value);
        if (error) {
          errors[fieldName] = error;
          break; // Stop at first error for this field
        }
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }
}

/**
 * Validation utilities
 */
export const ValidationUtils = {
  // Validate a single field
  validateField: (value, validators) => {
    for (const validator of validators) {
      const error = validator(value);
      if (error) return error;
    }
    return null;
  },

  // Validate form data against schema
  validateForm: (data, schema) => {
    return schema.validate(data);
  },

  // Get password strength
  getPasswordStrength: (password) => {
    if (!password) return { strength: 0, label: t("validation.pwNone"), color: "gray" };

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    score = Object.values(checks).filter(Boolean).length;

    const strengthMap = {
      0: { strength: 0, label: t("validation.pwVeryWeak"), color: "red" },
      1: { strength: 20, label: t("validation.pwWeak"), color: "red" },
      2: { strength: 40, label: t("validation.pwMedium"), color: "orange" },
      3: { strength: 60, label: t("validation.pwGood"), color: "yellow" },
      4: { strength: 80, label: t("validation.pwStrong"), color: "green" },
      5: { strength: 100, label: t("validation.pwVeryStrong"), color: "green" },
    };

    return strengthMap[score];
  },

  // Debounce validation for real-time validation
  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },
};

/**
 * Common validation schemas for Mawered
 *
 * NOTE: exposed as getters so messages are resolved lazily in the active language.
 */
export const CommonSchemas = {
  // Employee form validation
  get employee() {
    return new ValidationSchema()
      .field("nom")
      .required(t("validation.nameRequired"))
      .minLength(2)
      .maxLength(50)
      .field("prenom")
      .required(t("validation.firstNameRequired"))
      .minLength(2)
      .maxLength(50)
      .field("email")
      .email()
      .field("telephone")
      .phone()
      .field("code_badge")
      .custom(HRValidationRules.badgeCode)
      .field("salaire")
      .custom(HRValidationRules.salary);
  },

  // Company configuration validation - permissive version
  get company() {
    return new ValidationSchema()
      .field("legal_name")
      .required(t("validation.companyNameRequired"))
      .minLength(2)
      .field("email")
      .custom((value) => {
        if (!value || !value.trim()) return null;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return t("validation.emailInvalid");
        }
        return null;
      })
      .field("website")
      .custom((value) => {
        if (!value || !value.trim()) return null;

        let testUrl = value;
        if (!value.startsWith("http://") && !value.startsWith("https://")) {
          testUrl = "http://" + value;
        }

        try {
          new URL(testUrl);
          return null;
        } catch {
          return t("validation.urlFmtInvalid");
        }
      })
      .field("siret")
      .custom((value) => {
        if (!value || !value.trim()) return null;
        const siretRegex = /^\d{14}$/;
        if (!siretRegex.test(value)) {
          return t("validation.siretInvalid");
        }
        return null;
      })
      .field("siren")
      .custom((value) => {
        if (!value || !value.trim()) return null;
        const sirenRegex = /^\d{9}$/;
        if (!sirenRegex.test(value)) {
          return t("validation.sirenInvalid");
        }
        return null;
      })
      .field("legal_working_hours")
      .custom((value) => {
        if (!value) return null;
        const num = parseFloat(value);
        if (isNaN(num) || num < 20 || num > 80) {
          return t("validation.legalWorkingHours");
        }
        return null;
      })
      .field("annual_leave_days")
      .custom((value) => {
        if (!value) return null;
        const num = parseInt(value);
        if (isNaN(num) || num < 10 || num > 60) {
          return t("validation.annualLeave");
        }
        return null;
      })
      .field("logo")
      .custom((value) => {
        if (!value || !value.trim()) return null;

        if (value.startsWith("/uploads/")) {
          const imageExtensions = [".png", ".jpg", ".jpeg", ".svg", ".gif", ".webp"];
          const hasValidExtension = imageExtensions.some((ext) => value.toLowerCase().endsWith(ext));
          if (!hasValidExtension) {
            return t("validation.logoNotImage");
          }
          return null;
        }

        if (value.startsWith("data:")) {
          if (value.length > 7340032) {
            return t("validation.logoTooLarge");
          }
          return null;
        }

        if (value.startsWith("http://") || value.startsWith("https://")) {
          try {
            new URL(value);
            return null;
          } catch {
            return t("validation.logoUrlInvalid");
          }
        }

        return t("validation.logoUnsupported");
      });
  },

  // User login validation
  get login() {
    return new ValidationSchema()
      .field("username")
      .required(t("validation.usernameRequired"))
      .minLength(3)
      .field("password")
      .required(t("validation.passwordRequired"))
      .minLength(6);
  },

  // Password change validation
  get passwordChange() {
    return new ValidationSchema()
      .field("currentPassword")
      .required(t("validation.currentPwRequired"))
      .field("newPassword")
      .required(t("validation.newPwRequired"))
      .custom(ValidationRules.password)
      .field("confirmPassword")
      .required(t("validation.confirmRequired"));
  },
};
